import React, { useEffect, useMemo, useState } from 'react';
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  Cell,
  Text,
} from 'recharts';
import {
  FaNewspaper,
  FaBookOpen,
  FaSatelliteDish,
  FaSyncAlt,
  FaUsers,
  FaCalendarAlt,
  FaBrain,
} from 'react-icons/fa';
import {
  TechShell,
  TechPageHeader,
  TechSection,
  StatStrip,
  StatTile,
  StatLabel,
  StatValue,
  StatSub,
  Grid,
  Span,
  TechInput,
  TechSelect,
  TechButton,
  TechButtonGhost,
  StatusPill,
  TechChip,
  Empty,
  Loading,
  ErrorBox,
  borderSoft,
  panelDeep,
  mono,
  accent,
} from '../../styles/techComponents';

import {
  listRecentNews,
  listScrapeRuns,
  listCompetitors,
  getCompetitorStats,
  getCompetitorNews,
} from '../../services/competitorsService';
import { listResearch, listInsights } from '../../services/researchService';
import { listPolls, createPoll, deletePoll } from '../../services/platformService';
import { FaPlus, FaTrashAlt, FaSave, FaPoll, FaTimes, FaExternalLinkAlt, FaChevronRight } from 'react-icons/fa';
import {
  LineChart,
  Line,
} from 'recharts';

const tickStyle = { fontSize: 11, fontFamily: mono, fill: 'var(--text-disabled)' };
const tooltipStyle = {
  background: 'rgba(11, 19, 32, 0.95)',
  border: '1px solid rgba(0, 180, 216, 0.4)',
  borderRadius: 4,
  fontFamily: mono,
  fontSize: 12,
  color: 'var(--text-primary)',
};
const legendStyle = { paddingTop: 10, fontFamily: mono, fontSize: 11, letterSpacing: '0.04em' };
const COLORS = ['#00B4D8', '#06D6A0', '#FFD166', '#A4B0C2', '#7C8AA0'];

// Cuantas filas pide el panel. Los graficos se calculan sobre ESTA muestra,
// no sobre toda la base: se declara abajo de cada grafico para no dar a
// entender que el grafico cubre el total indexado.
const NEWS_SAMPLE = 100;
const RESEARCH_SAMPLE = 20;

// Nota de procedencia al pie de cada grafico: de donde salen los numeros.
const SourceNote = ({ children }) => (
  <p style={{
    marginTop: 12,
    fontFamily: mono,
    fontSize: '0.62rem',
    letterSpacing: '0.14em',
    textTransform: 'uppercase',
    color: 'var(--text-disabled)',
    lineHeight: 1.6,
  }}>
    Fuente: {children}
  </p>
);

const fmt = (n) => Number(n || 0).toLocaleString('es-AR');

const fmtRel = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (isNaN(d)) return '—';
  const diff = Date.now() - d.getTime();
  const h = diff / 36e5;
  if (h < 1) return 'hace <1h';
  if (h < 24) return `hace ${Math.round(h)}h`;
  return d.toLocaleDateString('es-AR', { day: '2-digit', month: 'short' });
};

const CampaignStatistics = () => {
  const [data, setData] = useState({
    competitors: [],
    news: [],
    scrape: { runs: [], totals: {} },
    research: { items: [] },
    insights: { insights: [] },
    polls: [],
  });
  const [loadState, setLoadState] = useState({ loading: true, error: null });
  const [pollForm, setPollForm] = useState({ date: new Date().toISOString().slice(0, 10), metric: 'approval', value: '', segment: 'general', notes: '' });
  const [busy, setBusy] = useState(false);
  const [detail, setDetail] = useState({ open: false, slug: null, name: '', loading: false, error: null, stats: null, news: [] });

  const openDetail = async (slug, name) => {
    if (!slug) return;
    setDetail({ open: true, slug, name, loading: true, error: null, stats: null, news: [] });
    try {
      const [s, n] = await Promise.all([
        getCompetitorStats(slug).catch(() => null),
        getCompetitorNews(slug, 25).catch(() => ({ news: [] })),
      ]);
      setDetail((d) => (d.slug === slug ? { ...d, loading: false, stats: s, news: (n && n.news) || [] } : d));
    } catch (e) {
      setDetail((d) => (d.slug === slug ? { ...d, loading: false, error: e?.message || 'Error' } : d));
    }
  };
  const closeDetail = () => setDetail({ open: false, slug: null, name: '', loading: false, error: null, stats: null, news: [] });

  const load = () => {
    setLoadState({ loading: true, error: null });
    Promise.all([
      listCompetitors().catch(() => []),
      listRecentNews({ limit: NEWS_SAMPLE }).catch(() => ({ news: [] })),
      listScrapeRuns(10).catch(() => ({ runs: [], totals: {} })),
      listResearch(RESEARCH_SAMPLE).catch(() => ({ items: [] })),
      listInsights().catch(() => ({ insights: [] })),
      listPolls().catch(() => []),
    ])
      .then(([competitors, news, scrape, research, insights, polls]) => {
        setData({
          competitors: competitors || [],
          news: news.news || [],
          scrape: scrape || { runs: [], totals: {} },
          research,
          insights,
          polls: polls || [],
        });
        setLoadState({ loading: false, error: null });
      })
      .catch((e) => setLoadState({ loading: false, error: e?.message || 'Error' }));
  };

  useEffect(load, []);

  const handlePollSubmit = async (e) => {
    e.preventDefault();
    if (!pollForm.metric || pollForm.value === '' || !pollForm.date) return;
    setBusy(true);
    try {
      await createPoll({ ...pollForm, value: Number(pollForm.value) });
      setPollForm({ ...pollForm, value: '', notes: '' });
      load();
    } catch (err) {
      alert(err?.response?.data?.error || err?.message || 'Error');
    } finally {
      setBusy(false);
    }
  };

  const handlePollDelete = async (id) => {
    if (!window.confirm('¿Eliminar dato de encuesta?')) return;
    try {
      await deletePoll(id);
      load();
    } catch (e) {
      alert(e?.message || 'Error');
    }
  };

  const pollSeries = useMemo(() => {
    const polls = data.polls || [];
    const byMetric = {};
    for (const p of polls) {
      if (!byMetric[p.metric]) byMetric[p.metric] = [];
      byMetric[p.metric].push({
        date: p.date.slice(0, 10),
        value: Number(p.value),
        id: p.id,
        segment: p.segment,
      });
    }
    for (const m of Object.keys(byMetric)) {
      byMetric[m].sort((a, b) => (a.date < b.date ? -1 : 1));
    }
    return byMetric;
  }, [data.polls]);

  const allDates = useMemo(() => {
    const set = new Set();
    for (const p of data.polls) set.add(p.date.slice(0, 10));
    return [...set].sort();
  }, [data.polls]);

  const pollChartData = useMemo(() => {
    return allDates.map((date) => {
      const row = { date };
      for (const metric of Object.keys(pollSeries)) {
        const point = pollSeries[metric].find((p) => p.date === date && p.segment === 'general');
        if (point) row[metric] = point.value;
      }
      return row;
    });
  }, [allDates, pollSeries]);

  const newsByDay = useMemo(() => {
    const map = new Map();
    for (const n of data.news) {
      const ts = n.published_at || n.fetched_at;
      if (!ts) continue;
      const d = new Date(ts);
      if (isNaN(d)) continue;
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
      map.set(key, (map.get(key) || 0) + 1);
    }
    return [...map.entries()]
      .sort(([a], [b]) => (a < b ? -1 : 1))
      .slice(-30)
      .map(([key, value]) => {
        const d = new Date(key);
        return { name: d.toLocaleDateString('es-AR', { day: '2-digit', month: 'short' }), value };
      });
  }, [data.news]);

  const newsByCompetitor = useMemo(() => {
    // slug por nombre, para poder abrir el informe detallado desde la barra.
    const slugByName = {};
    for (const c of (data.competitors || [])) if (c.name) slugByName[c.name] = c.slug;
    const map = {};
    for (const n of data.news) {
      const name = n.competitor_name || n.competitor_slug || 'Otros';
      if (!map[name]) map[name] = { name, count: 0, slug: n.competitor_slug || slugByName[name] || null };
      map[name].count += 1;
      if (!map[name].slug) map[name].slug = n.competitor_slug || slugByName[name] || null;
    }
    return Object.values(map).sort((a, b) => b.count - a.count);
  }, [data.news, data.competitors]);

  const researchByCategory = useMemo(() => {
    const map = {};
    for (const it of (data.research?.items || [])) {
      const k = it.category || 'sin categoría';
      map[k] = (map[k] || 0) + 1;
    }
    return Object.entries(map).map(([name, value]) => ({ name, value }));
  }, [data.research]);

  const lastRun = data.scrape?.runs?.[0];
  // Total real en base (lo devuelve /scrape-runs), contra el tamaño de la
  // muestra que se trajo el panel. Si difieren, los gráficos son parciales.
  const totalArticles = data.scrape?.totals?.articles || 0;
  const newsIsPartial = totalArticles > data.news.length;

  if (loadState.loading) {
    return (
      <TechShell>
        <TechPageHeader title="Panel de Reputación" subtitle="Datos en vivo del monitoreo" dossier="REP://STATS/v2" />
        <Loading>Cargando estadísticas reales</Loading>
      </TechShell>
    );
  }
  if (loadState.error) {
    return (
      <TechShell>
        <TechPageHeader title="Panel de Reputación" dossier="REP://STATS/v2" />
        <ErrorBox>No se pudieron cargar los datos · {loadState.error}</ErrorBox>
      </TechShell>
    );
  }

  return (
    <TechShell>
      <TechPageHeader
        title="Panel de Reputación"
        subtitle="Análisis de cobertura mediática y producción de research · datos reales"
        dossier="REP://STATS/v2"
        actions={<TechButtonGhost type="button" onClick={load}><FaSyncAlt /> Refrescar</TechButtonGhost>}
      >
        <StatusPill>Última corrida <strong>{fmtRel(lastRun?.started_at)}</strong></StatusPill>
        <StatusPill>Notas última corrida <strong>+{lastRun?.articles_added ?? 0}</strong></StatusPill>
      </TechPageHeader>

      <StatStrip min="180px" style={{ marginBottom: '1.25rem' }}>
        <StatTile>
          <StatLabel><FaNewspaper /> Notas indexadas</StatLabel>
          <StatValue>{data.scrape?.totals?.articles ?? data.news.length}</StatValue>
          <StatSub>en DB</StatSub>
        </StatTile>
        <StatTile>
          <StatLabel><FaUsers /> Competidores</StatLabel>
          <StatValue>{data.competitors.length}</StatValue>
          <StatSub>monitoreados</StatSub>
        </StatTile>
        <StatTile>
          <StatLabel><FaBookOpen /> Research items</StatLabel>
          <StatValue>{data.research?.items?.length || 0}</StatValue>
          {/* Si llegaron tantos como se pidieron, el numero es el tope de la
              consulta y no el total real: decirlo en vez de darlo por total. */}
          <StatSub>
            {(data.research?.items?.length || 0) >= RESEARCH_SAMPLE
              ? `auto + manual · últimos ${RESEARCH_SAMPLE}`
              : 'auto + manual'}
          </StatSub>
        </StatTile>
        <StatTile>
          <StatLabel><FaBrain /> Insights IA</StatLabel>
          <StatValue>{data.insights?.insights?.length || 0}</StatValue>
          <StatSub>cron weekly</StatSub>
        </StatTile>
      </StatStrip>

      <Grid>
        <Span span={8} className="mobile-full-width">
          <TechSection title={`Volumen de noticias · últimos ${newsByDay.length} días con actividad`}>
            {newsByDay.length === 0 ? (
              <Empty>Sin actividad reciente. Esperá la próxima corrida del cron.</Empty>
            ) : (
              <div style={{ height: 280 }}>
                <ResponsiveContainer>
                  <AreaChart data={newsByDay} margin={{ top: 12, right: 16, left: 0, bottom: 0 }}>
                    <defs>
                      <linearGradient id="g-vol" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor={accent} stopOpacity={0.5} />
                        <stop offset="100%" stopColor={accent} stopOpacity={0.05} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="2 4" stroke="rgba(255,255,255,0.06)" />
                    <XAxis dataKey="name" stroke="var(--text-disabled)" tick={tickStyle} />
                    <YAxis stroke="var(--text-disabled)" tick={tickStyle} allowDecimals={false} />
                    <Tooltip contentStyle={tooltipStyle} />
                    <Area type="monotone" dataKey="value" name="Notas" stroke={accent} strokeWidth={2.4} fill="url(#g-vol)" />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            )}
            <SourceNote>
              notas que el scraper levanta de medios y portales · se agrupan por fecha de publicación
              {newsIsPartial ? ` · calculado sobre las ${fmt(data.news.length)} notas más recientes, no sobre las ${fmt(totalArticles)} indexadas` : ''}
              {lastRun?.started_at ? ` · última corrida ${fmtRel(lastRun.started_at)}` : ''}
            </SourceNote>
          </TechSection>

          <TechSection
            title="Cobertura por competidor"
            style={{ marginTop: '1.25rem' }}
            action={<TechChip><FaChevronRight style={{ fontSize: '0.6rem' }} /> clic en cualquier fila para ver el informe</TechChip>}
          >
            {newsByCompetitor.length === 0 ? (
              <Empty>Sin datos.</Empty>
            ) : (
              <div style={{ height: 280 }}>
                <ResponsiveContainer>
                  {/* El onClick va en el BarChart y no en el Bar: asi se puede
                      clickear cualquier punto de la fila. Puesto solo en el Bar,
                      el unico blanco era el rectangulo de color — el nombre del
                      competidor no respondia y las barras de valor bajo median
                      pocos pixeles, con lo que no habia forma de abrirlas. */}
                  <BarChart
                    data={newsByCompetitor}
                    layout="vertical"
                    margin={{ top: 5, right: 24, left: 110, bottom: 0 }}
                    style={{ cursor: 'pointer' }}
                    onClick={(e) => {
                      const d = e?.activePayload?.[0]?.payload;
                      if (d) openDetail(d.slug, d.name);
                    }}
                  >
                    <CartesianGrid strokeDasharray="2 4" stroke="rgba(255,255,255,0.06)" />
                    <XAxis type="number" stroke="var(--text-disabled)" tick={tickStyle} allowDecimals={false} />
                    {/* El label vive fuera del area del grafico, asi que el
                        onClick del BarChart no lo alcanza: se le pone uno
                        propio. Se usa <Text> de recharts (no <text> plano)
                        para no perder el wrap en dos lineas de los nombres
                        largos. */}
                    <YAxis
                      dataKey="name"
                      type="category"
                      stroke="var(--text-disabled)"
                      width={120}
                      tick={({ x, y, payload }) => {
                        const row = newsByCompetitor.find((r) => r.name === payload.value);
                        return (
                          <Text
                            x={x}
                            y={y}
                            width={112}
                            textAnchor="end"
                            verticalAnchor="middle"
                            fill="var(--text-disabled)"
                            fontSize={11}
                            fontFamily={mono}
                            style={{ cursor: 'pointer' }}
                            onClick={() => row && openDetail(row.slug, row.name)}
                          >
                            {payload.value}
                          </Text>
                        );
                      }}
                    />
                    <Tooltip contentStyle={tooltipStyle} cursor={{ fill: 'rgba(0, 180, 216, 0.06)' }} />
                    {/* background pinta la fila completa, dando un blanco de
                        clic de ancho fijo aunque la barra sea diminuta. */}
                    <Bar dataKey="count" name="Notas" background={{ fill: 'rgba(255,255,255,0.03)' }}>
                      {newsByCompetitor.map((d, i) => (
                        <Cell key={i} fill={COLORS[i % COLORS.length]} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
            <SourceNote>
              las mismas notas del gráfico de arriba, agrupadas por el competidor al que el scraper las atribuyó
              {newsIsPartial ? ` · son ${fmt(data.news.length)} notas, no el histórico completo: sirve para comparar entre competidores, no como total por competidor` : ''}
              {' '}· {fmt(data.competitors.length)} competidores monitoreados
            </SourceNote>
          </TechSection>
        </Span>

        <Span span={4} className="mobile-full-width">
          <TechSection title={`Próxima corrida del scraper`}>
            <div style={{ padding: '0.6rem 0', fontFamily: mono }}>
              <p style={{ fontSize: '0.65rem', letterSpacing: '0.18em', textTransform: 'uppercase', color: 'var(--text-disabled)', margin: '0 0 4px' }}>
                Próxima ejecución
              </p>
              <p style={{ fontSize: '1.1rem', color: 'var(--text-primary)', margin: 0 }}>
                {data.scrape?.schedule?.next_run_at
                  ? new Date(data.scrape.schedule.next_run_at).toLocaleString('es-AR', { weekday: 'long', day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })
                  : '—'}
              </p>
              <p style={{ fontSize: '0.7rem', color: 'var(--text-disabled)', marginTop: 8 }}>
                cron: {data.scrape?.schedule?.cron || '—'}
              </p>
            </div>
            <SourceNote>
              agenda del cron que sale a buscar notas en medios y portales · cada corrida suma las
              notas nuevas que encuentra a la base ({fmt(totalArticles)} acumuladas)
            </SourceNote>
          </TechSection>

          <TechSection title="Producción de research por categoría" style={{ marginTop: '1.25rem' }}>
            {researchByCategory.length === 0 ? (
              <Empty>Sin items de research aún.</Empty>
            ) : (
              <div style={{ height: 230 }}>
                <ResponsiveContainer>
                  <BarChart data={researchByCategory}>
                    <CartesianGrid strokeDasharray="2 4" stroke="rgba(255,255,255,0.06)" />
                    <XAxis dataKey="name" stroke="var(--text-disabled)" tick={tickStyle} />
                    <YAxis stroke="var(--text-disabled)" tick={tickStyle} allowDecimals={false} />
                    <Tooltip contentStyle={tooltipStyle} cursor={{ fill: 'rgba(0, 180, 216, 0.06)' }} />
                    <Bar dataKey="value" name="Items" fill={accent} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
            <SourceNote>
              items de research, cargados a mano o generados por la IA, agrupados por su categoría
              {(data.research?.items?.length || 0) >= RESEARCH_SAMPLE
                ? ` · el panel trae los últimos ${RESEARCH_SAMPLE}, así que puede haber más sin contar`
                : ''}
            </SourceNote>
          </TechSection>

        </Span>
      </Grid>

      <TechSection
        title={`Encuestas internas · ${data.polls.length} datapoints`}
        style={{ marginTop: '1.25rem' }}
        action={<TechChip><FaPoll /> carga manual</TechChip>}
      >
        <form onSubmit={handlePollSubmit} style={{
          marginBottom: 14,
          padding: 12,
          background: panelDeep,
          border: `1px solid ${borderSoft}`,
          borderRadius: 6,
          display: 'grid',
          gridTemplateColumns: 'repeat(5, 1fr) auto',
          gap: 8,
        }}>
          <TechInput type="date" value={pollForm.date} onChange={(e) => setPollForm({ ...pollForm, date: e.target.value })} />
          <TechSelect value={pollForm.metric} onChange={(e) => setPollForm({ ...pollForm, metric: e.target.value })}>
            <option value="approval">Aprobación</option>
            <option value="awareness">Conocimiento</option>
            <option value="favorability">Favorabilidad</option>
            <option value="intent">Intención de voto</option>
            <option value="image">Imagen</option>
          </TechSelect>
          <TechInput type="number" step="0.1" placeholder="Valor (%)" value={pollForm.value} onChange={(e) => setPollForm({ ...pollForm, value: e.target.value })} />
          <TechInput placeholder="Segmento (general, 18-29…)" value={pollForm.segment} onChange={(e) => setPollForm({ ...pollForm, segment: e.target.value })} />
          <TechInput placeholder="Notas" value={pollForm.notes} onChange={(e) => setPollForm({ ...pollForm, notes: e.target.value })} />
          <TechButton type="submit" disabled={busy}>
            <FaSave /> {busy ? '…' : 'Cargar'}
          </TechButton>
        </form>

        {pollChartData.length === 0 ? (
          <Empty>
            No hay datos de encuestas cargados todavía. Usá el formulario de arriba para sumar el primer datapoint.
          </Empty>
        ) : (
          <>
            <div style={{ height: 280 }}>
              <ResponsiveContainer>
                <LineChart data={pollChartData}>
                  <CartesianGrid strokeDasharray="2 4" stroke="rgba(255,255,255,0.06)" />
                  <XAxis dataKey="date" stroke="var(--text-disabled)" tick={tickStyle} />
                  <YAxis stroke="var(--text-disabled)" tick={tickStyle} domain={[0, 100]} />
                  <Tooltip contentStyle={tooltipStyle} />
                  <Legend wrapperStyle={legendStyle} />
                  {Object.keys(pollSeries).map((m, i) => (
                    <Line
                      key={m}
                      type="monotone"
                      dataKey={m}
                      stroke={COLORS[i % COLORS.length]}
                      strokeWidth={2.4}
                      dot={{ r: 3 }}
                      name={m}
                    />
                  ))}
                </LineChart>
              </ResponsiveContainer>
            </div>

            <div style={{ marginTop: 14, display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 8 }}>
              {[...data.polls]
                .sort((a, b) => (a.date < b.date ? 1 : -1))
                .slice(0, 12)
                .map((p) => (
                  <div key={p.id} style={{
                    padding: '0.6rem 0.7rem',
                    background: panelDeep,
                    border: `1px solid ${borderSoft}`,
                    borderRadius: 5,
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    gap: 6,
                  }}>
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontFamily: mono, fontSize: '0.62rem', letterSpacing: '0.16em', textTransform: 'uppercase', color: 'var(--text-disabled)' }}>
                        {p.metric} · {p.segment}
                      </div>
                      <div style={{ color: 'var(--text-primary)', fontSize: '0.95rem', fontFamily: mono }}>
                        {p.value}% <span style={{ color: 'var(--text-disabled)', fontSize: '0.7rem' }}>· {p.date.slice(0, 10)}</span>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => handlePollDelete(p.id)}
                      style={{
                        background: 'transparent',
                        border: `1px solid ${borderSoft}`,
                        color: 'var(--text-disabled)',
                        width: 26,
                        height: 26,
                        borderRadius: 5,
                        cursor: 'pointer',
                      }}
                    >
                      <FaTrashAlt style={{ fontSize: '0.7rem' }} />
                    </button>
                  </div>
                ))}
            </div>
          </>
        )}

        <p style={{ marginTop: 12, fontFamily: mono, fontSize: '0.62rem', letterSpacing: '0.18em', textTransform: 'uppercase', color: 'var(--text-disabled)' }}>
          Fuente: encuestas cargadas manualmente desde este panel
        </p>
      </TechSection>

      {detail.open && (
        <CompetitorReport detail={detail} onClose={closeDetail} />
      )}
    </TechShell>
  );
};

// Informe detallado de un competidor (modal). Datos reales de competitor_news
// vía /api/competitors/:slug/stats y /news.
const CompetitorReport = ({ detail, onClose }) => {
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const stats = detail.stats?.stats;
  const sources = detail.stats?.top_sources || [];
  const name = detail.stats?.competitor?.name || detail.name;
  const maxSrc = sources.reduce((m, s) => Math.max(m, s.count || 0), 0) || 1;

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, zIndex: 1000, background: 'rgba(4, 8, 14, 0.72)',
        backdropFilter: 'blur(3px)', display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
        padding: '4vh 1rem', overflowY: 'auto',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: '100%', maxWidth: 760, background: 'var(--bg-elevated, #0b1320)',
          border: `1px solid ${borderSoft}`, borderRadius: 10, boxShadow: '0 24px 60px rgba(0,0,0,0.5)',
        }}
      >
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12,
          padding: '0.9rem 1.1rem', borderBottom: `1px solid ${borderSoft}`,
        }}>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontFamily: mono, fontSize: '0.6rem', letterSpacing: '0.2em', textTransform: 'uppercase', color: 'var(--text-disabled)' }}>
              Informe de competidor
            </div>
            <h3 style={{ margin: '2px 0 0', color: 'var(--text-primary)', fontSize: '1.15rem' }}>{name}</h3>
          </div>
          <button type="button" onClick={onClose} aria-label="Cerrar" style={{
            background: 'transparent', border: `1px solid ${borderSoft}`, color: 'var(--text-secondary)',
            width: 34, height: 34, borderRadius: 7, cursor: 'pointer', flexShrink: 0,
          }}><FaTimes /></button>
        </div>

        <div style={{ padding: '1.1rem' }}>
          {detail.loading ? (
            <Loading>Cargando informe…</Loading>
          ) : detail.error ? (
            <ErrorBox>{detail.error}</ErrorBox>
          ) : (
            <>
              <StatStrip min="130px" style={{ marginBottom: '1.1rem' }}>
                <StatTile><StatLabel>Total notas</StatLabel><StatValue>{stats?.total ?? '—'}</StatValue><StatSub>histórico</StatSub></StatTile>
                <StatTile><StatLabel>Últimos 7 días</StatLabel><StatValue>{stats?.last_7d ?? '—'}</StatValue><StatSub>notas</StatSub></StatTile>
                <StatTile><StatLabel>Últimos 30 días</StatLabel><StatValue>{stats?.last_30d ?? '—'}</StatValue><StatSub>notas</StatSub></StatTile>
                <StatTile><StatLabel>Última nota</StatLabel><StatValue style={{ fontSize: '0.95rem' }}>{fmtRel(stats?.last_article_at)}</StatValue><StatSub>más reciente</StatSub></StatTile>
              </StatStrip>

              {sources.length > 0 && (
                <div style={{ marginBottom: '1.1rem' }}>
                  <div style={{ fontFamily: mono, fontSize: '0.62rem', letterSpacing: '0.18em', textTransform: 'uppercase', color: 'var(--text-disabled)', marginBottom: 8 }}>
                    Medios que más lo cubren
                  </div>
                  {sources.map((s, i) => (
                    <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
                      <div style={{ width: 150, flexShrink: 0, fontSize: '0.8rem', color: 'var(--text-secondary)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{s.source || '—'}</div>
                      <div style={{ flex: 1, height: 8, background: 'rgba(255,255,255,0.05)', borderRadius: 4, overflow: 'hidden' }}>
                        <div style={{ width: `${Math.round((s.count / maxSrc) * 100)}%`, height: '100%', background: COLORS[i % COLORS.length] }} />
                      </div>
                      <div style={{ width: 30, textAlign: 'right', fontFamily: mono, fontSize: '0.78rem', color: 'var(--text-primary)' }}>{s.count}</div>
                    </div>
                  ))}
                </div>
              )}

              <div style={{ fontFamily: mono, fontSize: '0.62rem', letterSpacing: '0.18em', textTransform: 'uppercase', color: 'var(--text-disabled)', marginBottom: 8 }}>
                Notas recientes ({detail.news.length})
              </div>
              {detail.news.length === 0 ? (
                <Empty>Sin notas para este competidor.</Empty>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {detail.news.map((n) => (
                    <a
                      key={n.id}
                      href={n.url}
                      target="_blank"
                      rel="noreferrer"
                      style={{
                        display: 'block', padding: '0.6rem 0.7rem', background: panelDeep,
                        border: `1px solid ${borderSoft}`, borderRadius: 6, textDecoration: 'none',
                      }}
                    >
                      <div style={{ color: 'var(--text-primary)', fontSize: '0.86rem', lineHeight: 1.4, marginBottom: 4 }}>
                        {n.title} <FaExternalLinkAlt style={{ fontSize: '0.62rem', color: accent, marginLeft: 2 }} />
                      </div>
                      <div style={{ fontFamily: mono, fontSize: '0.68rem', color: 'var(--text-disabled)' }}>
                        {n.source || 'medio'} · {fmtRel(n.published_at || n.fetched_at)}
                      </div>
                    </a>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default CampaignStatistics;
